import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import type { RtParticipantInfo } from '../rt/use-rt-room'
import type { AnalyzerSnap, UseLocalAnalyzerResult } from '../rt/use-analyzer'
import { Panel } from './slp/Panel'
import { StatusLamp, type LampStatus } from './slp/StatusLamp'
import '../styles/slp.css'

/**
 * Client Analyzer v2.4 — the exact 15-parameter table (name · live value ·
 * threshold lamp · 60 s mini-sparkline). Actor/director ends render their own
 * full table (ClientAnalyzerPro); the studio renders the all-participants
 * matrix (ClientAnalyzerMatrix) fed by the 8-item heartbeat summaries plus
 * the v2.7 #8 Device / AEC echo rows.
 */

// ---------------------------------------------------------------------------
// Sparkline — 60×16 canvas, last 60 samples, NaN gaps skipped
// ---------------------------------------------------------------------------
function Spark({ values, color = '#5b8def' }: { values: number[] | undefined; color?: string }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const cv = ref.current
    if (!cv) return
    const ctx = cv.getContext('2d')
    if (!ctx) return
    const W = cv.width
    const H = cv.height
    ctx.clearRect(0, 0, W, H)
    const vals = (values ?? []).filter((v) => Number.isFinite(v))
    if (vals.length < 2 || !values) return
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const v of values) {
      if (!Number.isFinite(v)) continue
      if (v < min) min = v
      if (v > max) max = v
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return
    const span = max - min || 1
    ctx.strokeStyle = color
    ctx.lineWidth = 1
    ctx.beginPath()
    let started = false
    const n = values.length
    for (let i = 0; i < n; i++) {
      const v = values[i]
      if (v == null || !Number.isFinite(v)) {
        started = false
        continue
      }
      const x = (i / Math.max(1, n - 1)) * (W - 2) + 1
      const y = H - 2 - ((v - min) / span) * (H - 4)
      if (started) ctx.lineTo(x, y)
      else {
        ctx.moveTo(x, y)
        started = true
      }
    }
    ctx.stroke()
  }, [values, color])
  // re-render every paint: parent re-renders 1/s with new snap
  return <canvas ref={ref} width={60} height={16} className="block" aria-hidden="true" />
}

// ---------------------------------------------------------------------------
// Threshold helpers (spec v2.4 table, per row)
// ---------------------------------------------------------------------------
function bad(v: number, warn: number, error: number): LampStatus {
  return v > error ? 'error' : v > warn ? 'warn' : 'ok'
}

type Row = {
  key: string
  label: string
  value: string
  status: LampStatus
  sparkKey?: string
  onClick?: () => void
  title?: string
}

function fmtInt(v: number | null): string {
  return v == null ? '—' : v.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

export function buildRows(
  s: AnalyzerSnap,
  targetKbps: number,
  role: string,
  resetClip: () => void,
): Row[] {
  const rows: Row[] = []
  // 1 RTT — ≤150 / 151–300 / >300
  rows.push({
    key: 'rtt',
    label: 'RTT',
    value: s.rttMs == null ? '—' : `${fmtInt(s.rttMs)} ms`,
    status: s.rttMs == null ? 'off' : bad(s.rttMs, 150, 300),
    sparkKey: 'rtt',
  })
  // 2 loss up — <1 / 1–5 / >5
  rows.push({
    key: 'lossUp',
    label: 'Loss ↑ (send)',
    value: s.lossUpPct == null ? '—' : `${s.lossUpPct.toFixed(1)} %`,
    status: s.lossUpPct == null ? 'off' : s.lossUpPct > 5 ? 'error' : s.lossUpPct >= 1 ? 'warn' : 'ok',
    sparkKey: 'lossUp',
  })
  // 3 loss down
  rows.push({
    key: 'lossDown',
    label: 'Loss ↓ (recv)',
    value: s.lossDownPct == null ? '—' : `${s.lossDownPct.toFixed(1)} %`,
    status:
      s.lossDownPct == null ? 'off' : s.lossDownPct > 5 ? 'error' : s.lossDownPct >= 1 ? 'warn' : 'ok',
    sparkKey: 'lossDown',
  })
  // 4 jitter — ≤30 / >30
  rows.push({
    key: 'jitter',
    label: 'Jitter',
    value: s.jitterMs == null ? '—' : `${s.jitterMs.toFixed(1)} ms`,
    status: s.jitterMs == null ? 'off' : s.jitterMs > 30 ? 'warn' : 'ok',
    sparkKey: 'jitter',
  })
  // 5 actual send bitrate — ≥80% / <80% / <50% of target
  rows.push({
    key: 'sendKbps',
    label: 'Audio bitrate ↑',
    value: s.sendKbps == null ? '—' : `${fmtInt(s.sendKbps)} kbps`,
    status:
      s.sendKbps == null
        ? 'off'
        : s.sendKbps < targetKbps * 0.5
          ? 'error'
          : s.sendKbps < targetKbps * 0.8
            ? 'warn'
            : 'ok',
    sparkKey: 'sendKbps',
    title: `target ${targetKbps} kbps`,
  })
  // 6 available outgoing — 3 falling samples → yellow
  rows.push({
    key: 'availOut',
    label: 'Uplink avail',
    value:
      s.availOutKbps == null
        ? '—'
        : `${fmtInt(s.availOutKbps)} kbps ${s.availTrend === 'down' ? '↘' : '→'}`,
    status: s.availOutKbps == null ? 'off' : s.availTrend === 'down' ? 'warn' : 'ok',
    sparkKey: 'availOut',
  })
  // 7 link type — relay amber
  rows.push({
    key: 'linkType',
    label: 'Link',
    value: s.linkType == null ? '—' : s.linkType === 'relay' ? 'TURN relay' : s.linkType,
    status: s.linkType == null ? 'off' : s.linkType === 'relay' ? 'warn' : 'ok',
  })
  // 8 video receive (actor)
  if (role === 'actor' || s.vidRecvKbps != null) {
    const freezes = s.vidFreezeCount ?? 0
    rows.push({
      key: 'vidRecv',
      label: 'Video ↓',
      value:
        s.vidRecvKbps == null
          ? '—'
          : `${fmtInt(s.vidRecvKbps)} kbps · drop ${fmtInt(s.vidFramesDropped ?? 0)} · frz ${fmtInt(freezes)}`,
      status: s.vidRecvKbps == null ? 'off' : freezes >= 1 ? 'warn' : 'ok',
      sparkKey: 'vidRecv',
    })
  }
  // 9 sample rate — 48000 green else RED (recording spec broken)
  rows.push({
    key: 'sampleRate',
    label: 'Sample rate',
    value: s.sampleRate == null ? '—' : `${fmtInt(s.sampleRate)} Hz`,
    status: s.sampleRate == null ? 'off' : s.sampleRate === 48000 ? 'ok' : 'error',
  })
  // 10 worklet dropouts — 0 green / ≥1 red
  rows.push({
    key: 'worklet',
    label: 'Worklet drops',
    value: s.workletDropped == null ? '—' : fmtInt(s.workletDropped),
    status: s.workletDropped == null ? 'off' : s.workletDropped >= 1 ? 'error' : 'ok',
  })
  // 11 input peak + clip latch (click resets)
  rows.push({
    key: 'peak',
    label: 'Input peak',
    value: `${s.peakDbfs <= -100 ? '-∞' : s.peakDbfs.toFixed(1)} dBFS${s.clipCount > 0 ? ` · clip ×${s.clipCount}` : ''}`,
    status: s.clipCount > 0 ? 'error' : s.peakDbfs > -3 ? 'warn' : 'ok',
    sparkKey: 'peak',
    onClick: s.clipCount > 0 ? resetClip : undefined,
    title: s.clipCount > 0 ? 'Click to reset the clip latch' : undefined,
  })
  if (role === 'actor') {
    // 12 recorder state
    rows.push({
      key: 'recState',
      label: 'Recorder',
      value: s.recState === 'recording' ? 'REC' : s.recState,
      status: s.recState === 'recording' ? (s.recStalled ? 'error' : 'ok') : 'off',
    })
    // 13 bytes written — REC + >3 s no growth → red
    rows.push({
      key: 'recMB',
      label: 'Written',
      value: `${(s.recBytes / 1_048_576).toFixed(1)} MB`,
      status: s.recState === 'recording' ? (s.recStalled ? 'error' : 'ok') : 'off',
      sparkKey: 'recMB',
      title: s.recStalled ? 'No byte growth for >3 s — write stalled' : undefined,
    })
    // 14 commit latency — ≤200 / 201–500 / >500 ms
    rows.push({
      key: 'commit',
      label: 'Disk commit',
      value: s.commitMs == null ? '—' : `${fmtInt(s.commitMs)} ms`,
      status: s.commitMs == null ? 'off' : bad(s.commitMs, 200, 500),
      sparkKey: 'commit',
    })
    // 15 disk free — ≥need / <need×1.5 / <0.5 GB or <need
    const free = s.diskFreeGB
    rows.push({
      key: 'diskGB',
      label: 'Disk free',
      value: free == null ? '—' : `${free.toFixed(1)} GB · need ~${s.diskNeedGB.toFixed(1)} GB`,
      status:
        free == null
          ? 'off'
          : free < 0.5 || free < s.diskNeedGB
            ? 'error'
            : free < s.diskNeedGB * 1.5
              ? 'warn'
              : 'ok',
      sparkKey: 'diskGB',
    })
  }
  return rows
}

export function ClientAnalyzerPro({
  analyzer,
  role,
  targetBitrateKbps,
  subject = 'You',
  className,
}: {
  analyzer: UseLocalAnalyzerResult
  role: string
  targetBitrateKbps: number
  subject?: string
  className?: string
}) {
  const rows = buildRows(analyzer.snap, targetBitrateKbps, role, analyzer.resetClip)
  return (
    <Panel title="Client Analyzer" className={className}>
      <div data-testid="rt-client-analyzer" data-rows={rows.length}>
        <div className="mb-1.5 text-[12px] font-semibold text-[var(--slp-text-dim)]">{subject}</div>
        <table className="w-full border-collapse text-[12px]">
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                data-metric={r.key}
                data-status={r.status}
                title={r.title}
                onClick={r.onClick}
                className={clsx(
                  'border-b border-[var(--slp-border)] last:border-b-0',
                  r.onClick && 'cursor-pointer',
                )}
              >
                <td className="py-0.5 pr-1 text-[var(--slp-text-dim)]">{r.label}</td>
                <td
                  className={clsx(
                    'py-0.5 pr-1 text-right font-mono tabular-nums',
                    r.status === 'error'
                      ? 'font-semibold text-[var(--slp-lamp-red)]'
                      : r.status === 'warn'
                        ? 'font-semibold text-[#b07b00]'
                        : 'text-[var(--slp-text)]',
                  )}
                >
                  {r.value}
                </td>
                <td className="w-[62px] py-0.5">
                  {r.sparkKey ? <Spark values={analyzer.history.get(r.sparkKey)} /> : null}
                </td>
                <td className="w-4 py-0.5 text-right">
                  <StatusLamp status={r.status} size="sm" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Studio matrix — one column per participant, fed by heartbeat summaries
// ---------------------------------------------------------------------------
const MATRIX_ROWS: Array<{
  key: string
  label: string
  fmt: (p: RtParticipantInfo) => string
  lamp: (p: RtParticipantInfo) => LampStatus
}> = [
  {
    key: 'rtt',
    label: 'RTT',
    fmt: (p) => (p.status?.sum?.rttMs == null ? '—' : `${p.status.sum.rttMs}ms`),
    lamp: (p) => (p.status?.sum?.rttMs == null ? 'off' : bad(p.status.sum.rttMs, 150, 300)),
  },
  {
    key: 'lossUp',
    label: 'Loss↑',
    fmt: (p) => (p.status?.sum?.lossUpPct == null ? '—' : `${p.status.sum.lossUpPct.toFixed(1)}%`),
    lamp: (p) => {
      const v = p.status?.sum?.lossUpPct
      return v == null ? 'off' : v > 5 ? 'error' : v >= 1 ? 'warn' : 'ok'
    },
  },
  {
    key: 'lossDown',
    label: 'Loss↓',
    fmt: (p) => (p.status?.sum?.lossDownPct == null ? '—' : `${p.status.sum.lossDownPct.toFixed(1)}%`),
    lamp: (p) => {
      const v = p.status?.sum?.lossDownPct
      return v == null ? 'off' : v > 5 ? 'error' : v >= 1 ? 'warn' : 'ok'
    },
  },
  {
    key: 'jitter',
    label: 'Jitter',
    fmt: (p) => (p.status?.sum?.jitterMs == null ? '—' : `${p.status.sum.jitterMs.toFixed(1)}ms`),
    lamp: (p) => {
      const v = p.status?.sum?.jitterMs
      return v == null ? 'off' : v > 30 ? 'warn' : 'ok'
    },
  },
  {
    key: 'bitrate',
    label: 'Bitrate↑',
    fmt: (p) => (p.status?.sum?.bitrateKbps == null ? '—' : `${p.status.sum.bitrateKbps}k`),
    lamp: (p) => (p.status?.sum?.bitrateKbps == null ? 'off' : 'ok'),
  },
  {
    key: 'sampleRate',
    label: 'SR',
    fmt: (p) => (p.status?.sum?.sampleRate == null ? '—' : `${p.status.sum.sampleRate}`),
    lamp: (p) =>
      p.status?.sum?.sampleRate == null ? 'off' : p.status.sum.sampleRate === 48000 ? 'ok' : 'error',
  },
  {
    key: 'disk',
    label: 'Disk',
    fmt: (p) => (p.status?.sum?.diskFreeGB == null ? '—' : `${p.status.sum.diskFreeGB.toFixed(1)}GB`),
    lamp: (p) => {
      const v = p.status?.sum?.diskFreeGB
      return v == null ? 'off' : v < 1.6 ? 'error' : v < 2.4 ? 'warn' : 'ok'
    },
  },
  {
    key: 'rec',
    label: 'REC',
    fmt: (p) => {
      const st = p.status?.rec?.state
      const b = p.status?.sum?.recBytes ?? p.status?.rec?.bytes
      if (st !== 'recording') return st === 'armed' ? 'armed' : '—'
      return b == null ? 'REC' : `REC ${(b / 1_048_576).toFixed(1)}MB`
    },
    lamp: (p) => (p.status?.rec?.state === 'recording' ? 'error' : 'off'),
  },
]

function trunc(s: string | undefined, n = 22): string {
  if (!s) return '—'
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

export function ClientAnalyzerMatrix({
  participants,
  className,
}: {
  participants: RtParticipantInfo[]
  className?: string
}) {
  return (
    <Panel title="Client Analyzer" className={className}>
      <div data-testid="rt-client-analyzer" data-matrix="1" className="overflow-x-auto">
        <table className="w-full border-collapse text-[11px]">
          <thead>
            <tr className="text-left text-[var(--slp-text-dim)]">
              <th className="py-0.5 pr-1 font-semibold" />
              {participants.map((p) => (
                <th key={p.identity} className="max-w-[90px] truncate py-0.5 pr-1 font-semibold">
                  {p.name}
                  {p.isLocal ? ' (you)' : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MATRIX_ROWS.map((row) => (
              <tr key={row.key} data-metric={row.key} className="border-t border-[var(--slp-border)]">
                <td className="py-0.5 pr-1 text-[var(--slp-text-dim)]">{row.label}</td>
                {participants.map((p) => {
                  const status = row.lamp(p)
                  return (
                    <td key={p.identity} className="whitespace-nowrap py-0.5 pr-1 font-mono tabular-nums">
                      <span className="mr-1 inline-block align-middle">
                        <StatusLamp status={status} size="sm" />
                      </span>
                      <span
                        className={clsx(
                          status === 'error'
                            ? 'font-semibold text-[var(--slp-lamp-red)]'
                            : status === 'warn'
                              ? 'font-semibold text-[#b07b00]'
                              : 'text-[var(--slp-text)]',
                        )}
                      >
                        {row.fmt(p)}
                      </span>
                    </td>
                  )
                })}
              </tr>
            ))}
            {/* v2.7 #8 — device + AEC echo rows */}
            <tr data-metric="device" className="border-t border-[var(--slp-border)]">
              <td className="py-0.5 pr-1 text-[var(--slp-text-dim)]">Device</td>
              {participants.map((p) => (
                <td
                  key={p.identity}
                  data-testid={`rt-remote-device-${p.identity}`}
                  title={`in: ${p.status?.dev?.inputLabel ?? '—'}\nout: ${p.status?.dev?.outputLabel ?? '—'}`}
                  className="max-w-[120px] truncate py-0.5 pr-1"
                >
                  {trunc(p.status?.dev?.inputLabel)}
                </td>
              ))}
            </tr>
            <tr data-metric="aec" className="border-t border-[var(--slp-border)]">
              <td className="py-0.5 pr-1 text-[var(--slp-text-dim)]">AEC</td>
              {participants.map((p) => {
                const aec = p.status?.dev?.aec
                const amber = aec === true && p.role === 'actor' // actors should record AEC-off
                return (
                  <td
                    key={p.identity}
                    data-aec={aec == null ? '' : String(aec)}
                    className={clsx(
                      'py-0.5 pr-1 font-semibold',
                      amber ? 'bg-[#fff3d6] text-[#b07b00]' : 'text-[var(--slp-text)]',
                    )}
                    title={amber ? 'Actor has echo cancellation ON — recommend OFF for recording' : undefined}
                  >
                    {aec == null ? '—' : aec ? (amber ? 'ON ⚠' : 'ON') : 'off'}
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  )
}
